import { useGetAttachmentsByDocId } from "@/api/attachment";
import {
  getRelativePosition,
  useGetAuction,
  useUpdateAuction,
} from "@/api/auction/auction";
import {
  appendNoDuplicateSaleNumbersRefine,
  auctionSchema,
} from "@/api/auction/auction-validation";
import * as AF from "@/components/form-items/AbstractForm";
import { ModifiableList } from "@/components/form-items/ModifiableList";
import LinksManagement from "@/components/LinksManagement";
import AddFileModal from "@/components/modal/AddFileModal";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/context/auth/AuthContext";
import { toast } from "@/hooks/use-toast";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { getMainLanguage } from "@/i18n";
import { FormLayout } from "@/layout/FormLayout";
import { computeFieldAuctionSummary } from "@/lib/computeFields";
import { Auction } from "@/lib/types";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

const schema = appendNoDuplicateSaleNumbersRefine(auctionSchema.passthrough());

const EditPublicAuction = ({ auction }: { auction: Auction }) => {
  const { instanceData } = useAuth();

  const form = useForm({
    defaultValues: auction,
    resolver: standardSchemaResolver(schema),
    reValidateMode: "onSubmit",
  });

  const isModulePhotoEnabled = useMemo(() => {
    return instanceData?.isModulePhotoEnabled;
  }, [instanceData?.isModulePhotoEnabled]);

  const { data: attachments } = useGetAttachmentsByDocId(auction.id);

  const auctionUpdater = useUpdateAuction();

  const { t } = useTranslation();

  const handleResume = (data: any) => {
    return computeFieldAuctionSummary(data, {
      language: getMainLanguage(),
    });
  };

  const { relativePosition } = useDashboardParams<Auction>({
    statusField: "ventesPublicationStatus",
    resourceName: "auction",
    relativePosition: {
      currentId: auction.id,
      positionFetcher: getRelativePosition,
    },
  });

  const queryClient = useQueryClient();

  const [, setSearchParam] = useSearchParams();

  return (
    <FormLayout
      title={auction.ventesDescriptionTitre}
      type="auction"
      relativePosition={relativePosition}
      onSave={(status) =>
        form.handleSubmit(
          (values) => {
            auctionUpdater.mutate(
              {
                ...values,
                ...(status && { ventesPublicationStatus: status }),
              },
              {
                onSuccess: () => {
                  toast({
                    title: t(
                      status === undefined
                        ? "autoSave"
                        : "recordSavedToastTitle",
                    ),
                    variant: "success",
                    description: t("auctionSavedToastDescription"),
                  });
                  form.reset({}, { keepValues: true });
                },
              },
            );
          },
          (invalid) => {
            if (invalid.ventesDescriptionNumeroVente != undefined)
              setSearchParam({ tab: "description" });
          },
        )()
      }
      invalidateOnChange={() => {
        queryClient.invalidateQueries({
          queryKey: ["auction", auction.id],
        });
        queryClient.invalidateQueries({ queryKey: ["auctions"] });

        queryClient.invalidateQueries({
          queryKey: ["attachment", { docId: auction.id }],
        });
      }}
      publicationStatus={auction.ventesPublicationStatus}
      id={auction.id}
      reference={auction.ventesDescriptionNumeroVente}
      picturesData={attachments && { pictures: attachments, type: "auction" }}
      form={form}
    >
      <Separator />
      <AF.AbstractForm
        form={form}
        tabs={
          [
            { value: "description", label: t("auctionDescription") },
            { value: "sources", label: t("sources") },
            { value: "files", label: t("fichiers") },
            { value: "links", label: t("linkedEntries") },
          ] as const
        }
      >
        <AF.Tab value="description">
          <AF.Col>
            <AF.Row>
              <AF.Text name="ventesDescriptionNumeroVente" className="w-64" />
              <AF.Checkbox name="ventesDescriptionCatalogue" align={true} />
            </AF.Row>
            <AF.Text name="ventesDescriptionTitre" />
            <AF.Text className="w-64" name="ventesDescriptionDate" />
            <ModifiableList
              name="ventesDescriptionCommissaires"
              listKey="liste-maisons-de-vente"
            />
            <ModifiableList
              name="ventesDescriptionLieu"
              listKey="liste-lieux-de-vente"
            />
            <AF.Text className="w-16" name="ventesDescriptionSalle" />
            <AF.Row>
              <ModifiableList
                name="ventesDescriptionVille"
                listKey="liste-villes"
              />
              <ModifiableList
                name="ventesDescriptionPays"
                listKey="liste-pays"
              />
            </AF.Row>
          </AF.Col>
          <AF.Col>
            <AF.RichText
              name="ventesDescriptionResume"
              computeAction={handleResume}
            />
            <AF.RichText name="ventesDescriptionNotesVente" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="sources">
          <AF.RichText name="sources" />
        </AF.Tab>
        <AF.Tab
          isForm={false}
          value="files"
          headerExtras={<AddFileModal id={auction.id} />}
        >
          <AF.OthersFilesTable docId={auction.id} name="diversfiles" />
        </AF.Tab>
        <AF.Tab value="links">
          <LinksManagement
            data={auction}
            id={auction.id}
            type="auction"
            configuration={{
              fields: [
                {
                  name: "links",
                  type: "links",
                  label: "Liens",
                  links: [
                    {
                      name: "artworkInAuctions",
                      label: t("entryartworks"),
                      type: "link-complex",
                      fields: [
                        {
                          name: "auction.ventesDescriptionNumeroVente",
                          label: t("auctionDescriptionNumeroVente"),
                          type: "text",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "auction.ventesDescriptionResume",
                          label: t("auctionDescriptionResume"),
                          type: "richtext",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "oeuvresDansVentesDescriptionNumeroLot",
                          label: t("oeuvresDansVentesDescriptionNumeroLot"),
                          type: "text",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionReproduitPage",
                          label: t("oeuvresDansVentesDescriptionReproduitPage"),
                          type: "text",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionEstimationBasse",
                          label: t(
                            "oeuvresDansVentesDescriptionEstimationBasse",
                          ),
                          type: "inputSelectCurrency",
                          className: "w-full",
                          defaultSelectValue: "EUR",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionEstimationHaute",
                          label: t(
                            "oeuvresDansVentesDescriptionEstimationHaute",
                          ),
                          type: "inputSelectCurrency",
                          className: "w-full",
                          defaultSelectValue: "EUR",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionVendu",
                          label: t("oeuvresDansVentesDescriptionVendu"),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionResultatNonListe",
                          label: t(
                            "oeuvresDansVentesDescriptionResultatNonListe",
                          ),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionNonVenu",
                          label: t("oeuvresDansVentesDescriptionNonVenu"),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionNonVendu",
                          label: t("oeuvresDansVentesDescriptionNonVendu"),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionNonReproduit",
                          label: t("oeuvresDansVentesDescriptionNonReproduit"),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionRetire",
                          label: t("oeuvresDansVentesDescriptionRetire"),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionMiseAPrix",
                          label: t("oeuvresDansVentesDescriptionMiseAPrix"),
                          type: "inputSelectCurrency",
                          defaultSelectValue: "EUR",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionPrixMarteau",
                          label: t("oeuvresDansVentesDescriptionPrixMarteau"),
                          type: "inputSelectCurrency",
                          defaultSelectValue: "EUR",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionPrixAvecFrais",
                          label: t("oeuvresDansVentesDescriptionPrixAvecFrais"),
                          type: "inputSelectCurrency",
                          defaultSelectValue: "EUR",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionNotes",
                          label: t("oeuvresDansVentesDescriptionNotes"),
                          type: "richtext",
                          className: "w-full",
                        },
                      ],
                    },
                    {
                      name: "tirageInAuction",
                      label: t("entrytirages"),
                      type: "link-complex",
                      hidden: !isModulePhotoEnabled,
                      fields: [
                        {
                          name: "auction.ventesDescriptionNumeroVente",
                          label: t("auctionDescriptionNumeroVente"),
                          type: "text",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "auction.ventesDescriptionResume",
                          label: t("auctionDescriptionResume"),
                          type: "richtext",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "tirageDansVentesDescriptionNumeroLot",
                          label: t("tirageDansVentesDescriptionNumeroLot"),
                          type: "text",
                          className: "w-full",
                        },
                        {
                          name: "tirageDansVentesDescriptionReproduitPage",
                          label: t("tirageDansVentesDescriptionReproduitPage"),
                          type: "text",
                          className: "w-full",
                        },
                        {
                          name: "tirageDansVentesDescriptionEstimationBasse",
                          label: t(
                            "tirageDansVentesDescriptionEstimationBasse",
                          ),
                          type: "inputSelectCurrency",
                          className: "w-full",
                          defaultSelectValue: "EUR",
                        },
                        {
                          name: "tirageDansVentesDescriptionEstimationHaute",
                          label: t(
                            "tirageDansVentesDescriptionEstimationHaute",
                          ),
                          type: "inputSelectCurrency",
                          className: "w-full",
                          defaultSelectValue: "EUR",
                        },
                        {
                          name: "tirageDansVentesDescriptionVendu",
                          label: t("tirageDansVentesDescriptionVendu"),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "tirageDansVentesDescriptionResultatNonListe",
                          label: t(
                            "tirageDansVentesDescriptionResultatNonListe",
                          ),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "tirageDansVentesDescriptionNonVenu",
                          label: t("tirageDansVentesDescriptionNonVenu"),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "tirageDansVentesDescriptionNonVendu",
                          label: t("tirageDansVentesDescriptionNonVendu"),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "tirageDansVentesDescriptionNonReproduit",
                          label: t("tirageDansVentesDescriptionNonReproduit"),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "tirageDansVentesDescriptionRetire",
                          label: t("tirageDansVentesDescriptionRetire"),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "tirageDansVentesDescriptionMiseAPrix",
                          label: t("tirageDansVentesDescriptionMiseAPrix"),
                          type: "inputSelectCurrency",
                          defaultSelectValue: "EUR",
                          className: "w-full",
                        },
                        {
                          name: "tirageDansVentesDescriptionPrixMarteau",
                          label: t("tirageDansVentesDescriptionPrixMarteau"),
                          type: "inputSelectCurrency",
                          defaultSelectValue: "EUR",
                          className: "w-full",
                        },
                        {
                          name: "tirageDansVentesDescriptionPrixAvecFrais",
                          label: t("tirageDansVentesDescriptionPrixAvecFrais"),
                          type: "inputSelectCurrency",
                          defaultSelectValue: "EUR",
                          className: "w-full",
                        },
                        {
                          name: "tirageDansVentesDescriptionNotes",
                          label: t("tirageDansVentesDescriptionNotes"),
                          type: "richtext",
                          className: "w-full",
                        },
                      ],
                    },
                    {
                      name: "relatedBibliography",
                      label: t("entrybibliographies"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedAudiovisual",
                      label: t("entryaudiovisuals"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedDocument",
                      label: t("entrydocuments"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedWriting",
                      label: t("entrywritings"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedPhotography",
                      label: t("entryphotographies"),
                      type: "link-simple",
                    },
                  ],
                },
              ],
            }}
          />
        </AF.Tab>
      </AF.AbstractForm>
    </FormLayout>
  );
};

export const EditPublicAuctionPage = () => {
  const { id } = useParams();

  const { data, isLoading } = useGetAuction(id);

  return (
    <>
      {data && <EditPublicAuction key={data.id} auction={data} />}
      {!data && !isLoading && <h2 className="p-4">venteNExistePasLabel</h2>}
    </>
  );
};
